import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { campaignApi } from '@/api/campaign';
import { LayoutGrid, List } from 'lucide-preact';
import { CampaignWebsCardView } from '../webs/CampaignWebsCardView';
import { CampaignWebsListView } from '../webs/CampaignWebsListView';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
export function CampaignWebsTab({ campaign, active = true }) {
    const [cards, setCards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState(null);
    const { updateContext } = useAIChatContextUpdater();
    // Update AI context when viewing campaign webs
    useEffect(() => {
        if (!active || !cards)
            return;
        const totalWebs = cards.Webs.length;
        const totalLeads = cards.Webs.reduce((sum, web) => sum + (web.LeadsCount || 0), 0);
        const totalGoal = cards.Webs.reduce((sum, web) => sum + (web.LeadGoal || 0), 0);
        updateContext({
            workspaceId: campaign.WorkspaceId,
            campaignId: campaign.Id,
            location: ChatLocations.CampaignWebs,
            entityType: ChatEntityTypes.Campaign,
            entityId: campaign.Id,
            viewMode: ChatViewModes.View,
            availableFields: [
                {
                    name: 'totalWebs',
                    displayName: 'Total Webs',
                    fieldType: 'number',
                    currentValue: totalWebs.toString(),
                    isEditable: false,
                    isFocused: false,
                },
                {
                    name: 'totalLeads',
                    displayName: 'Total Leads',
                    fieldType: 'number',
                    currentValue: totalLeads.toString(),
                    isEditable: false,
                    isFocused: false,
                },
                {
                    name: 'totalGoal',
                    displayName: 'Total Goal',
                    fieldType: 'number',
                    currentValue: totalGoal.toString(),
                    isEditable: false,
                    isFocused: false,
                },
                {
                    name: 'viewMode',
                    displayName: 'View Mode',
                    fieldType: 'text',
                    currentValue: viewMode || 'cards',
                    isEditable: false,
                    isFocused: false,
                },
            ],
            loadedData: {
                webs: cards.Webs.map(web => ({
                    id: web.Id,
                    title: web.Title,
                    webType: web.WebType,
                    leadsCount: web.LeadsCount || 0,
                    leadGoal: web.LeadGoal || 0,
                    launchDate: web.LaunchDate,
                    lastLeadCreatedDate: web.LastLeadCreatedDate,
                })),
            },
            availableActions: [
                'Create New Web',
                'View Web Details',
                'Filter Webs by Type',
                'Sort Webs',
            ],
        });
    }, [active, cards, viewMode, campaign, updateContext]);
    useEffect(() => {
        if (!active)
            return;
        const load = async () => {
            setLoading(true);
            setError(null);
            const res = await campaignApi.GetCampaignWebCards(campaign.ClientCompanyId, campaign.WorkspaceId, campaign.Id);
            if (res.data) {
                setCards(res.data);
                // Set default view based on web count
                if (viewMode === null) {
                    setViewMode(res.data.Webs.length > 9 ? 'list' : 'cards');
                }
            }
            else
                setError(res.error || 'Failed to load webs');
            setLoading(false);
        };
        load();
    }, [active, campaign.Id, campaign.WorkspaceId, campaign.ClientCompanyId]);
    if (loading) {
        return (_jsxs("div", { className: "flex items-center gap-3 text-slate-600 dark:text-slate-300", children: [_jsx("div", { className: "w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Loading webs..."] }));
    }
    if (error) {
        return _jsx("div", { className: "text-red-600 dark:text-red-400", children: error });
    }
    if (!cards || cards.Webs.length === 0) {
        const assetsBase = import.meta.env.VITE_ASSETS_BASE_URL || '';
        const imgSrc = `${assetsBase}/assets/demand-web.png`;
        return (_jsxs("div", { className: "flex flex-col items-center justify-center py-16", children: [_jsx("p", { className: "text-center text-slate-600 dark:text-slate-300 text-sm md:text-base", children: "Use the New button to add some WEBS." }), _jsx("br", {}), _jsx("img", { src: imgSrc, alt: "Add some Webs", className: "w-96 h-auto mb-6 opacity-90 dark:opacity-80", loading: "lazy", onError: (e) => { e.currentTarget.style.display = 'none'; } })] }));
    }
    // Sort webs by LaunchDate (newest first) for both views
    const sortedWebs = useMemo(() => {
        const list = [...cards.Webs];
        list.sort((a, b) => {
            const dateA = new Date(a.LaunchDate).getTime();
            const dateB = new Date(b.LaunchDate).getTime();
            return dateB - dateA;
        });
        return list;
    }, [cards, viewMode]);
    return (_jsxs("div", { children: [_jsx("div", { className: "mb-4 flex items-center justify-end", children: _jsxs("div", { className: "inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-1", children: [_jsxs("button", { onClick: () => setViewMode('cards'), className: `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'cards'
                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`, children: [_jsx(LayoutGrid, { className: "w-4 h-4" }), "Cards"] }), _jsxs("button", { onClick: () => setViewMode('list'), className: `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list'
                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`, children: [_jsx(List, { className: "w-4 h-4" }), "List"] })] }) }), viewMode === 'cards' ? (_jsx(CampaignWebsCardView, { webs: sortedWebs, campaignId: campaign.Id, workspaceId: campaign.WorkspaceId, clientCompanyId: campaign.ClientCompanyId })) : (_jsx(CampaignWebsListView, { webs: sortedWebs, campaignId: campaign.Id, workspaceId: campaign.WorkspaceId, clientCompanyId: campaign.ClientCompanyId }))] }));
}
